'use client'
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { User } from '@/types/models/User'
import { useAuthMe } from '@/hooks/useAuthMe.hook'
import { useAuth } from './auth.provider'

export interface ContextImpersonate {
  accountId?: number
  setAccountId: (id?: number) => void
  shouldBlock: boolean
}

const ImpersonateContext = createContext<ContextImpersonate | undefined>(
  undefined
)

interface ImpersonateProviderProps {
  children: ReactNode
}

const ImpersonateProvider = ({ children }: ImpersonateProviderProps) => {
  const { authenticated } = useAuth()
  const { user } = useAuthMe({ authenticated })
  const [accountId, setAccountId] = useState<number | undefined>(undefined)
  const shouldBlock = useMemo(
    () => user?.role === 'GOD' && !accountId,
    [user?.role, accountId]
  )
  const handleSetAccountId = useCallback(
    (id?: number) => {
      if (user?.role !== 'GOD') {
        throw new Error(
          'Only users with role GOD can use the ImpersonateProvider'
        )
      }
      setAccountId(id)
    },
    [user?.role]
  )

  const context = useMemo(
    () => ({
      accountId,
      setAccountId: handleSetAccountId,
      shouldBlock,
    }),
    [accountId, handleSetAccountId, shouldBlock]
  )

  return (
    <ImpersonateContext.Provider value={context}>
      {children}
    </ImpersonateContext.Provider>
  )
}

function useImpersonate(): ContextImpersonate {
  const context = useContext(ImpersonateContext)
  if (context === undefined) {
    throw new Error('useImpersonate must be used within an ImpersonateProvider')
  }
  return context
}

export { ImpersonateProvider, useImpersonate }
